import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import {
  ICustomer,
  IPayment,
  IPrePurchaseOrder,
  IProduct,
  IPurchaseOrder,
  ISale,
} from "../common/interfaces";
import { v4 } from "uuid";
import { getLinePrice } from "../services/pricing";

interface PosState {
  order: ISale;
  customer: ICustomer;
  products: IProduct[];
  payments: IPayment[];
  authorizations: any[];
  listProducts: IProduct[];
  listCustomers: ICustomer[];
  exchangeRate: number;
  purchaseOrder: {
    [key:string]: IPrePurchaseOrder
  };
}

interface PosActions {
  setOrder: (by: ISale) => void;
  setProducts: (by: IProduct[]) => void;
  addProduct: (by: IProduct) => void;
  setListProducts: (by: IProduct[]) => void;
  setCustomer: (by: ICustomer) => void;
  setAuthorizations: (by: any) => void;
  reset: () => void;
  clearOrder: () => void;
  setListCustomers: (by: ICustomer[]) => void;
  setExchangeRate: (by: number) => void;
  setPurchaseOrder: (supplierId: string, by: IPrePurchaseOrder) => void;
  clearPurchaseOrder: (supplierId: string) => void;
}

export const initialState: PosState = {
  order: {
    id: 0,
    cliente_id: 0,
    usuario_id: 0,
    fecha: "",
    totalUSD: "",
    descuento: "",
    total: "",
    subtotal: "",
    iva: "",
    ieps: "",
    metodoPago: "",
    formaPago: "",
    usoCFDI: "",
    notas: "",
    notes_order: "",
    created_at: "",
    updated_at: "",
    status: "",
    sale_type: "",
    origin_by: "",
    cliente: {
      id: 1,
      nombre: "Público en General",
      rfc: "XAXX010101000",
      direccion: "Sin dirección",
      codigoPostal: "88710",
      regimenFiscal: "616",
      usoCFDI: "S01",
      email: "",
      updated_at: "",
    },
    facturas: [],
    detalles: [],
  },
  customer: {
    id: 1,
    nombre: "Público en General",
    rfc: "XAXX010101000",
    direccion: "Sin dirección",
    codigoPostal: "88710",
    regimenFiscal: "616",
    usoCFDI: "S01",
    email: "",
    updated_at: "",
  },
  products: [],
  payments: [],
  authorizations: [],
  listProducts: [],
  listCustomers: [],
  exchangeRate: 0,
  purchaseOrder: {},
};

export const usePosStore = create<PosState & PosActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setOrder: (by) => set({ order: by }),
        setProducts: (by) => {
          // Sellar la identidad del renglon AQUI, que es el unico paso por el que se
          // escribe el carrito. Un renglon sin line_uid hace que `a === b` empareje a
          // todos los que tampoco lo tienen: un solo tecleo de cantidad escribia la
          // misma cantidad en todo el carrito, y cancelar un renglon los borraba todos.
          const products = by.map((product) =>
            product.line_uid ? product : { ...product, line_uid: v4() }
          );
          set({ products });

          // Verificar si hay algún producto con cantidad inválida
          const hasInvalidQuantity = products.some((product) => {
            if (
              product.quantity === '' ||
              product.quantity === '0' ||
              product.quantity === '0.' ||
              product.quantity === null ||
              product.quantity === undefined
            ) {
              return true;
            }
            const quantity = Number(product.quantity);
            return isNaN(quantity) || quantity <= 0;
          });

          // Si hay productos con cantidad inválida, NO recalcular totales
          if (hasInvalidQuantity) {
            console.log('⚠️ Hay productos con cantidad inválida - NO se recalculan totales');
            return;
          }

          // Si todos los productos tienen cantidad válida, recalcular totales
          let subTotal = 0;
          let iva = 0;
          let ieps = 0;
          let discount = 0;
          let total = 0;
          console.log('✅ Todos los productos válidos - Recalculando totales...')

          products.forEach((product) => {
            const quantity = Number(product.quantity);
            // El precio incluye el delta de los modificadores elegidos. Este calculo y el de
            // cartView no comparten codigo, por eso los dos usan getLinePrice.
            const price = getLinePrice(product);

            const linePrice = Number((price * quantity).toFixed(2));
            subTotal += linePrice;
            iva += Number(((product.iva?.porcentaje ?? 0) * linePrice).toFixed(2));
            ieps += Number(((product.ieps?.porcentaje ?? 0) * linePrice).toFixed(2));
            discount += product.discount ?? 0;
          });
          total = subTotal + iva + ieps - discount;
          console.log({ total, subTotal, iva, ieps, discount })
          const totalUSD = Number(total / get().exchangeRate).toFixed(2);
          set({
            order: {
              ...get().order,
              total: total.toFixed(2),
              subtotal: subTotal.toFixed(2),
              iva: iva.toFixed(2),
              ieps: ieps.toFixed(2),
              descuento: discount.toFixed(2),
              totalUSD: totalUSD,
            },
          });
        },
        addProduct: (by) => set({ products: [by] }),
        setListProducts: (by) => set({ listProducts: by }),
        setCustomer: (by) => set({ customer: by }),
        setAuthorizations: (by) => set({ authorizations: by }),
        reset: () => set(initialState),
        clearOrder: () =>
          set({
            order: initialState.order,
            customer: initialState.customer,
            products: initialState.products,
            payments: initialState.payments,
          }),
        clearPurchaseOrder: (supplierId: string) => {
          const curr = get().purchaseOrder;
          delete curr[supplierId];
          set({ purchaseOrder: curr });
        },
        setListCustomers: (by) => set({ listCustomers: by }),
        setExchangeRate: (by) => set({ exchangeRate: by }),
        setPurchaseOrder: (supplierId: string, by: IPrePurchaseOrder) => {
          const po = get().purchaseOrder
          set({ purchaseOrder: { ...po, [supplierId]: by } })
        },
      }),
      {
        name: "pos-storage",
        // Los carritos guardados por builds anteriores no tienen line_uid. Sin esta
        // migracion se renderizan con key={undefined} y cancelar un renglon borra el
        // equivocado, porque ahora todo se empareja por line_uid y no por product id.
        //
        // version 2: la v1 solo sello lo que ya estaba guardado en ese momento. Como el
        // alta de un producto sin opciones seguia entrando sin line_uid, los carritos que
        // hay ahorita en las cajas volvieron a acumular renglones sin identidad y la
        // migracion ya no corria sobre ellos. Subirla los limpia al desplegar, antes de
        // que la cajera alcance a teclear la primera cantidad.
        version: 2,
        migrate: (persisted: any): PosState & PosActions =>
          ({
            ...persisted,
            products: (persisted?.products ?? []).map((product: IProduct) =>
              product?.line_uid ? product : { ...product, line_uid: v4() }
            ),
          }) as PosState & PosActions,
      }
    )
  )
);
